import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, Users, ClipboardCheck, Loader2, Mail, Building2, MoreHorizontal, UserMinus, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuthRoles } from "@/hooks/useAuthRoles";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { useAuth } from "@/context/Auth";

interface AssessmentUsersTabProps {
  assessmentId: string;
  portfolioId: string;
  portfolioName: string;
  readOnly?: boolean;
}

interface UserInfo {
  user_id: string;
  email: string;
  display_name: string | null;
  role: string;
  status: "invited" | "active" | "submitted" | "completed";
  scope: "assessment" | "portfolio"; // Whether assigned to this assessment or just portfolio member
}

interface AssessmentOption {
  id: string;
  status: string;
  questionnaire_id: string;
}

export function AssessmentUsersTab({
  assessmentId,
  portfolioId,
  portfolioName,
  readOnly = false,
}: AssessmentUsersTabProps) {
  const { toast } = useToast();
  const { roles: currentUserRoles } = useAuthRoles();
  const { role: effectiveRole } = useEffectiveRole();
  const { tier } = useAuth();
  const isTier1PM = effectiveRole === "portfolio_manager" && tier === "tier_1";
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [assessments, setAssessments] = useState<AssessmentOption[]>([]);
  const [showPortfolioWide, setShowPortfolioWide] = useState(false);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRole, setModalRole] = useState<"respondent" | "reviewer">("respondent");
  const [modalEmail, setModalEmail] = useState("");
  const [modalDisplayName, setModalDisplayName] = useState("");
  const [modalAssessmentId, setModalAssessmentId] = useState(assessmentId);
  const [tempPassword, setTempPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // Unassign confirmation state
  const [unassignUser, setUnassignUser] = useState<UserInfo | null>(null);
  const [unassigning, setUnassigning] = useState(false);
  
  // Can current user manage users?
  const canManageUsers =
    effectiveRole === "admin" ||
    effectiveRole === "client_admin" ||
    effectiveRole === "org_admin" ||
    effectiveRole === "portfolio_manager";

  // DEBUG: Log on mount to confirm this component is rendering
  console.log("[AssessmentUsersTab] mounted", {
    effectiveRole,
    canManageUsers,
    readOnly,
    assessmentId,
    portfolioId,
  });

  // Fetch users and assessments
  const fetchData = async () => {
    setLoading(true);
    
    console.log("[UsersTab] effectiveRole:", effectiveRole, { assessmentId, portfolioId });
    console.log("[UsersTab] fetching users via: list_assessment_users RPC");
    
    try {
      // Use RPC function that handles authorization for all roles
      const { data, error } = await supabase.rpc("list_assessment_users", {
        p_assessment_id: assessmentId,
      });

      console.log("[UsersTab] list_assessment_users", { data, error });
      if (error) throw error;

      // Map RPC result to UserInfo structure
      const userList: UserInfo[] = (data ?? []).map((row: any) => ({
        user_id: row.user_id,
        email: row.email ?? "Unknown",
        display_name: row.display_name ?? null,
        role: row.role ?? "respondent",
        status: "active" as const, // RPC returns assigned users, so they're active
        scope: (row.scope ?? "assessment") as "assessment" | "portfolio",
      }));

      setUsers(userList);

      // Fetch all assessments for this portfolio (for the add user modal)
      // Check if admin to use RPC path
      const { data: isAdminRow } = await (supabase.rpc as any)("current_user_is_admin");
      const isAdmin = !!isAdminRow;
      
      if (isAdmin) {
        console.log("[UsersTab] admin assessments via RPC: admin_list_assessments_for_org");
        const { data: rpcData, error: rpcError } = await (supabase.rpc as any)("admin_list_assessments_for_org", {
          p_org_id: portfolioId,
        });
        
        if (rpcError) {
          console.error("Error fetching assessments via RPC:", rpcError);
        } else {
          setAssessments((rpcData || []).map((a: any) => ({
            id: a.id,
            questionnaire_id: a.questionnaire_id,
            status: a.status,
          })));
        }
      } else {
        // Non-admin: use PM RPC
        const { data: pmAssessments, error: pmAssErr } = await (supabase.rpc as any)(
          "pm_list_assessments_for_portfolio",
          { p_portfolio_id: portfolioId }
        );
        if (pmAssErr) {
          console.error("[UsersTab] pm_list_assessments_for_portfolio error:", pmAssErr);
        }
        setAssessments((pmAssessments ?? []).map((a: any) => ({
          id: a.id,
          questionnaire_id: a.questionnaire_id,
          status: a.status,
        })));
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      toast({
        variant: "destructive",
        title: "Failed to load users",
        description: "Please try refreshing the page.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [assessmentId, portfolioId]);

  const openAddModal = (role: "respondent" | "reviewer") => {
    setModalRole(role);
    setModalEmail("");
    setModalDisplayName("");
    setTempPassword("");
    setModalAssessmentId(assessmentId);
    setModalOpen(true);
  };

  const handleAddUser = async () => {
    // Hard UI guard: block submit if required fields missing
    if (!portfolioId) {
      toast({
        variant: "destructive",
        title: "Portfolio ID missing",
        description: "Cannot create user without a valid portfolio context.",
      });
      return;
    }

    if (!modalRole) {
      toast({
        variant: "destructive",
        title: "Role required",
        description: "Please select a role (respondent or reviewer).",
      });
      return;
    }

    if (!modalEmail.trim()) {
      toast({
        variant: "destructive",
        title: "Email required",
        description: "Please enter a valid email address.",
      });
      return;
    }

    if (!tempPassword.trim()) {
      toast({
        variant: "destructive",
        title: "Password required",
        description: "Please enter a password (min 6 characters).",
      });
      return;
    }

    if (!modalAssessmentId) {
      toast({
        variant: "destructive",
        title: "Assessment required",
        description: "Please select an assessment.",
      });
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        portfolio_id: portfolioId,
        email: modalEmail.trim().toLowerCase(),
        password: tempPassword,
        display_name: modalDisplayName?.trim() || null,
        member_role: modalRole || "respondent",
        assessment_id: modalAssessmentId,
      };
      console.log("[create-user payload]", payload);

      if (!payload.member_role) {
        toast({ variant: "destructive", title: "Role is required" });
        setSubmitting(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-user", {
        body: payload,
      });

      console.log("[AssessmentUsersTab] edge function result:", { data, error });

      if (error) {
        throw new Error(error.message ?? "Edge function error");
      }
      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "User added",
        description: `${modalRole === "respondent" ? "Respondent" : "Reviewer"} ${modalEmail.trim()} created — Password set (testing mode)`,
      });

      setModalOpen(false);
      setModalEmail("");
      setModalDisplayName("");
      setTempPassword("");
      setModalAssessmentId(assessmentId);
      fetchData(); // Refresh the list
    } catch (err: any) {
      console.error("[AssessmentUsersTab] RPC error:", err);
      toast({
        variant: "destructive",
        title: "Failed to add user",
        description: err?.message ?? "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle assigning an existing portfolio member to this assessment
  const handleAssignToAssessment = async (userId: string, role: string) => {
    if (!assessmentId) return;

    console.log("[UsersTab] assign start", { assessmentId, userId, role });

    const { error } = await (supabase.rpc as any)(
      "pm_assign_user_to_assessment",
      { p_assessment_id: assessmentId, p_user_id: userId, p_member_role: role }
    );

    console.log("[UsersTab] assign result", { error });

    if (error) {
      toast({ title: "Could not assign user", description: error.message, variant: "destructive" });
      return;
    }

    toast({
      title: "User assigned",
      description: "User has been assigned to this assessment.",
    });
    
    await fetchData();
  };

  // Handle unassigning a user from this assessment
  const handleUnassignUser = async () => {
    if (!unassignUser) return;
    
    setUnassigning(true);
    try {
      console.log("[UsersTab] unassign target", {
        assessmentId,
        unassignUser,
      });

      const targetUserId =
        unassignUser.user_id ??
        (unassignUser as any).id ??
        (unassignUser as any).assignee_user_id;

      if (!targetUserId) throw new Error("No user id found on row to unassign");

      console.log("[UsersTab] unassign start", { assessmentId, targetUserId });

      const { error } = await (supabase.rpc as any)(
        "pm_unassign_user_from_assessment",
        { p_assessment_id: assessmentId, p_user_id: targetUserId }
      );

      console.log("[UsersTab] unassign done", { error });

      if (error) throw error;

      // Optimistic UI update - remove from local state
      setUsers((prev) => prev.filter((u) => u.user_id !== targetUserId));

      toast({
        title: "User unassigned",
        description: `${unassignUser.display_name || unassignUser.email} has been removed from this assessment.`,
      });
      setUnassignUser(null);

      // Refetch for safety
      await fetchData();
    } catch (err: any) {
      console.error("[UsersTab] unassign failed", err);
      toast({
        variant: "destructive",
        title: "Action failed",
        description: err?.message ?? "Unknown error",
      });
    } finally {
      setUnassigning(false);
    }
  };

  // Handle changing a user's role on this assessment
  const handleChangeRole = async (userId: string, currentRole: string) => {
    if (!assessmentId) return;

    const nextRole = currentRole === "respondent" ? "reviewer" : "respondent";

    console.log("[UsersTab] changeRole start", { assessmentId, userId, currentRole, nextRole });

    // Re-assign with new role via PM RPC (handles upsert internally)
    const { error } = await (supabase.rpc as any)(
      "pm_assign_user_to_assessment",
      { p_assessment_id: assessmentId, p_user_id: userId, p_member_role: nextRole }
    );

    console.log("[UsersTab] changeRole result", { error });

    if (error) {
      toast({ title: "Could not change role", description: error.message, variant: "destructive" });
      return;
    }

    toast({
      title: "Role updated",
      description: `User role has been changed to ${nextRole}.`,
    });

    // Force refresh of the Users list
    await fetchData();
  };

  const getStatusBadge = (status: UserInfo["status"]) => {
    const configs = {
      invited: { label: "Invited", variant: "secondary" as const },
      active: { label: "Active", variant: "outline" as const },
      submitted: { label: "Submitted", variant: "default" as const },
      completed: { label: "Completed", variant: "default" as const },
    };
    const config = configs[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getScopeBadge = (scope: UserInfo["scope"]) => {
    if (scope === "assessment") {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-0">
          Assigned to this assessment
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="text-muted-foreground">
        Portfolio member only
      </Badge>
    );
  };

  // Filter users based on toggle and role
  const getFilteredUsers = (role: "respondent" | "reviewer") => {
    return users.filter(u => {
      const roleMatch = u.role === role;
      if (!showPortfolioWide) {
        // Only show assessment-scoped users
        return roleMatch && u.scope === "assessment";
      }
      // Show all users
      return roleMatch;
    });
  };

  const respondents = getFilteredUsers("respondent");
  const reviewers = getFilteredUsers("reviewer");

  // Count of portfolio-only members
  const portfolioOnlyCount = users.filter(u => u.scope === "portfolio").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading users...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* DEBUG marker - remove after testing */}
      <div className="text-xs text-red-500 bg-red-50 p-2 rounded border border-red-200">
        DEBUG UsersTab mounted — role: {String(effectiveRole)} — canManageUsers: {String(canManageUsers)} — readOnly: {String(readOnly)}
      </div>

      {/* Header with toggle */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing users assigned to this assessment.
        </p>
        {portfolioOnlyCount > 0 && (
          <div className="flex items-center gap-3">
            <Label htmlFor="show-portfolio" className="text-sm text-muted-foreground cursor-pointer">
              Show portfolio-wide members ({portfolioOnlyCount})
            </Label>
            <Switch
              id="show-portfolio"
              checked={showPortfolioWide}
              onCheckedChange={setShowPortfolioWide}
            />
          </div>
        )}
      </div>

      {/* Respondents Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Respondents
              <Badge variant="secondary" className="ml-2">{respondents.length}</Badge>
            </CardTitle>
            <div className="text-[10px] text-muted-foreground mt-1">
              debug: role={String(effectiveRole)} canManageUsers={String(canManageUsers)} readOnly={String(readOnly)}
            </div>
          </div>
          {canManageUsers && (
            <Button size="sm" onClick={() => openAddModal("respondent")}>
              <UserPlus className="h-4 w-4 mr-1" />
              Add Respondent
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {respondents.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {showPortfolioWide 
                ? "No respondents in this portfolio." 
                : "No respondents assigned to this assessment."}
            </p>
          ) : (
            <div className="divide-y">
              {respondents.map((user) => (
                <div
                  key={user.user_id}
                  className="flex items-center justify-between py-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                      {user.scope === "portfolio" ? (
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Mail className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm">
                        {user.display_name || user.email}
                      </div>
                      {user.display_name && (
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getScopeBadge(user.scope)}
                    {user.scope === "assessment" && getStatusBadge(user.status)}
                    {user.scope === "portfolio" && canManageUsers && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAssignToAssessment(user.user_id, user.role)}
                      >
                        Assign
                      </Button>
                    )}
                    {user.scope === "assessment" && canManageUsers && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleChangeRole(user.user_id, user.role)}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Change to Reviewer
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => setUnassignUser(user)}
                            className="text-destructive focus:text-destructive"
                          >
                            <UserMinus className="h-4 w-4 mr-2" />
                            Unassign from assessment
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reviewers Section */}
      {!isTier1PM && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5" />
                Reviewers
                <Badge variant="secondary" className="ml-2">{reviewers.length}</Badge>
              </CardTitle>
              <div className="text-[10px] text-muted-foreground mt-1">
                debug: role={String(effectiveRole)} canManageUsers={String(canManageUsers)} readOnly={String(readOnly)}
              </div>
            </div>
            {canManageUsers && (
              <Button size="sm" onClick={() => openAddModal("reviewer")}>
                <UserPlus className="h-4 w-4 mr-1" />
                Add Reviewer
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {reviewers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {showPortfolioWide 
                  ? "No reviewers in this portfolio." 
                  : "No reviewers assigned to this assessment."}
              </p>
            ) : (
              <div className="divide-y">
                {reviewers.map((user) => (
                  <div
                    key={user.user_id}
                    className="flex items-center justify-between py-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                        {user.scope === "portfolio" ? (
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Mail className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-sm">
                          {user.display_name || user.email}
                        </div>
                        {user.display_name && (
                          <div className="text-xs text-muted-foreground">{user.email}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getScopeBadge(user.scope)}
                      {user.scope === "assessment" && getStatusBadge(user.status)}
                      {user.scope === "portfolio" && canManageUsers && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleAssignToAssessment(user.user_id, user.role)}
                        >
                          Assign
                        </Button>
                      )}
                      {user.scope === "assessment" && canManageUsers && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleChangeRole(user.user_id, user.role)}>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Change to Respondent
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setUnassignUser(user)}
                              className="text-destructive focus:text-destructive"
                            >
                              <UserMinus className="h-4 w-4 mr-2" />
                              Unassign from assessment
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add User Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Add {modalRole === "respondent" ? "Respondent" : "Reviewer"}
            </DialogTitle>
            <DialogDescription>
              Assign a new {modalRole} to an assessment in {portfolioName}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={modalEmail}
                onChange={(e) => setModalEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                type="text"
                placeholder="John Doe"
                value={modalDisplayName}
                onChange={(e) => setModalDisplayName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tempPassword">Password *</Label>
              <Input
                id="tempPassword"
                type="password"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                placeholder="Min 6 characters"
              />
              <p className="text-xs text-muted-foreground">
                Required — user can log in immediately with this password
              </p>
            </div>

            {assessments.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="assessment">Assessment *</Label>
                <Select
                  value={modalAssessmentId}
                  onValueChange={setModalAssessmentId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select assessment" />
                  </SelectTrigger>
                  <SelectContent>
                    {assessments.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.questionnaire_id} ({a.status})
                        {a.id === assessmentId && " — Current"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  User will be assigned to the selected assessment only.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleAddUser} disabled={submitting || !modalEmail.trim() || !tempPassword.trim()}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add {modalRole === "respondent" ? "Respondent" : "Reviewer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unassign Confirmation Dialog */}
      <AlertDialog open={!!unassignUser} onOpenChange={(open) => !open && setUnassignUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove user from assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the user from THIS assessment only. They will remain available in the portfolio.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unassigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUnassignUser} 
              disabled={unassigning}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {unassigning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
